import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import YoutubeStatsGraphView from './youtube-stats-graph-view';
import { API_RESPONSE_TYPE, DateType, GROUP_BY_VALUE, lineChartDataType, TIME_WINDOWS, YOUTUBE_PAGES } from '../../types';
import { Option } from '@resi-media/resi-ui';
import { dateRangeValues, customDateRangeOption, lifeTimeValues } from '../../date-range-options';
import {
  downloadAsCSV,
  fetchAndMergeResponse,
  parseLineChartData,
  getDateRangeOptions,
} from '../../utils';
import { exportCSVData, fetchLineChartData, fetchLineChartOnDemandData } from '../api';
import { YoutubeContext } from '../../../../contexts/eventanalytics';
import {
  EVENT_ANALYTICS_CONTEXT_KEYS,
  GROUP_SET,
  MAX_FETCH_LIMIT_FOR_CSV_EXPORT,
  PAGE_VALUE,
  TIME_WINDOW_SET,
  TIME_WINDOW_VALUES,
  YOUTUBE_CSV_EXPORT_FIELDS,
  YOUTUBE_PAGE,
  YOUTUBE_PAGE_STATE_CONTEXT_KEYS,
} from '../../event-analytics-constants';
import { EventAnalyticsContext } from '../../../../contexts/eventanalytics/event-analytics';
import { Parser } from 'json2csv';
import { MPEventName, MPEventProperty, trackMixpanelEvent } from '../../../../mixpanel';

const YoutubeStatsGraph: FC = (): JSX.Element => {
  const { youtubeAnalyticsState , dispatch } = useContext(YoutubeContext);
  const { EventAnalyticsState, dispatch: dispatchEventAnalyticsState } = useContext(EventAnalyticsContext);
  const {
    viewAllData,
    eventId
  } = EventAnalyticsState;
  const [timeWindow, setTimeWindow] = useState<number>(0);
  const [isDatePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [dataType, setDataType] = useState<GROUP_BY_VALUE>(0);
  const [dataPage, setDataPage] = useState<YOUTUBE_PAGES>(0);
  const [dataPageName, setDataPageName] = useState<string>(YOUTUBE_PAGE[0]);
  const [timeWindowValue, setTimeWindowValue] = useState<string>(TIME_WINDOW_VALUES.DAY);
  const [chartData, setChartData] = useState<lineChartDataType>({ labels: [], datasets: {} });
  const [isChartLoading, setChartLoading] = useState<boolean>(false);
  const [isExportCSVLoading, setExportCSVLoading] = useState<boolean>(false);

  const handleGroupByChange = (event: ChangeEvent<unknown>, newValue: GROUP_BY_VALUE): void => {
    setDataType(newValue);
    dispatch({ type: YOUTUBE_PAGE_STATE_CONTEXT_KEYS.GROUP_BY, payload: GROUP_SET[newValue] });

    let dataSegmentType = '';

    switch (newValue) {
      case 0:
        dataSegmentType = 'None';
        break;
      case 1:
        dataSegmentType = 'Event Type';
        break;
      case 2:
        dataSegmentType = 'Device Type';
        break;
    }

    const mixpanelProps = {
      [MPEventProperty.TRANSCODED_EVENT_UUID]: EventAnalyticsState.eventId,
      [MPEventProperty.ANALYTICS_SEGMENT]: dataSegmentType,
      [MPEventProperty.ANALYTICS_TYPE]: 'YouTube',
    };

    if (!EventAnalyticsState.eventId) {
      delete mixpanelProps[MPEventProperty.TRANSCODED_EVENT_UUID];
    }

    trackMixpanelEvent(MPEventName.ANALYTICS_SEGMENT, mixpanelProps);
  };

  const handleTimeWindowChange = (event: ChangeEvent<unknown> | null, newValue: TIME_WINDOWS) => {
    setTimeWindowValue(TIME_WINDOW_SET[newValue]);
    setTimeWindow(newValue);
  };

  const handleDataPageChange = (event: ChangeEvent<unknown>, newValue: YOUTUBE_PAGES): void => {
    setDataPage(newValue);
    setDataPageName(YOUTUBE_PAGE[newValue]);
    dispatch({ type: YOUTUBE_PAGE_STATE_CONTEXT_KEYS.PAGE, payload: YOUTUBE_PAGE[newValue] });
  };

  const handleDateRangeChange = (item: Option[]): void => {
    const selection = item[0]?.value;
    let dateRange: DateType = { startDate: '', endDate: '' };
    // Since we have only one custom handling case "c0".
    if (selection === customDateRangeOption.value) {
      setDatePickerOpen(true);
    } else if (selection === '0' && viewAllData) {
      dateRange = lifeTimeValues[selection];
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE, payload: dateRange });
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE_VALUE, payload: item[0] });
    } else if (selection) {
      dateRange = dateRangeValues[selection];
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE, payload: dateRange });
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE_VALUE, payload: item[0] });
    }
  };

  const handleCustomDateRangeSelection = (startDate: string, endDate: string) => {
    if (startDate && endDate) {
      const dateRange = {
        startDate,
        endDate,
      };
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE, payload: dateRange });
      dispatchEventAnalyticsState({
        type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE_VALUE,
        payload: customDateRangeOption,
      });
    }
  };

  const exportCSV = async () => {
    setExportCSVLoading(true);
    const response = await fetchAndMergeResponse(
      (offSetValue1: string | number | undefined, offSetValue2: string | number | undefined) => exportCSVData(EventAnalyticsState, offSetValue1, offSetValue2),
      MAX_FETCH_LIMIT_FOR_CSV_EXPORT,
      'videoId',
      'timestamp'
    );
    setExportCSVLoading(false);
    const json2csvParser = new Parser({ fields: YOUTUBE_CSV_EXPORT_FIELDS });
    const csv = json2csvParser.parse(response);
    downloadAsCSV(csv, 'Youtube-Analytics.csv');

    const mixpanelProps = {
      [MPEventProperty.TRANSCODED_EVENT_UUID]: EventAnalyticsState.eventId,
      [MPEventProperty.ANALYTICS_TYPE]: 'YouTube',
    };

    if (!EventAnalyticsState.eventId) {
      delete mixpanelProps[MPEventProperty.TRANSCODED_EVENT_UUID];
    }

    trackMixpanelEvent(MPEventName.CSV_EXPORT, mixpanelProps);
  };

  const getChartData = async () => {
    let response : API_RESPONSE_TYPE;
    let lineChartData : lineChartDataType = {labels: [] , datasets: {}};
    setChartLoading(true);
    if ( dataPageName !== PAGE_VALUE.LIVE_VIEWS ){
        response = await fetchLineChartOnDemandData(EventAnalyticsState,youtubeAnalyticsState, timeWindowValue);
        lineChartData = parseLineChartData(response, timeWindowValue);
    } else {
        response = await fetchLineChartData(EventAnalyticsState, timeWindowValue);
        lineChartData = eventId === null ? parseLineChartData(response, timeWindowValue) : parseLineChartData(response, timeWindowValue, dataPageName);
    }
    setChartLoading(false);
    setChartData(lineChartData);
  };

  useEffect(() => {
    getChartData();
  }, [youtubeAnalyticsState, timeWindowValue, EventAnalyticsState]);

  return (
    <YoutubeStatsGraphView
      data={chartData}
      dataPageName={dataPageName}
      dataType={dataType}
      dataPage={dataPage}
      dateRangeOptions={getDateRangeOptions(viewAllData)}
      isChartLoading={isChartLoading}
      isDatePickerOpen={isDatePickerOpen}
      timeWindow={timeWindow}
      handleGroupByChange={handleGroupByChange}
      handleDataPageChange={handleDataPageChange}
      handleDateRangeChange={handleDateRangeChange}
      handleTimeWindowChange={handleTimeWindowChange}
      handleCustomDateRangeSelection={handleCustomDateRangeSelection}
      exportCSV={exportCSV}
      isExportCSVLoading={isExportCSVLoading}
      closeDatePicker={() => setDatePickerOpen(false)}
    />
  );
};

YoutubeStatsGraph.displayName = 'YoutubeStatsGraph';

export default YoutubeStatsGraph;
